import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { ABOUT_INFO, TESTIMONIALS, FAQ_DATA, CONTACT_INFO } from '@/mocks';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SchemaOrg type="Organization" data={{
        name: ABOUT_INFO.brandName,
        url: 'https://777cinema.com',
        description: ABOUT_INFO.tagline,
        foundingDate: ABOUT_INFO.founded,
        contactPoint: {
          '@type': 'ContactPoint',
          email: CONTACT_INFO.email,
          telephone: CONTACT_INFO.phone,
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: '777影院 - 关于我们',
        description: '了解777影院的品牌故事、用户口碑和联系方式。777影院午夜伦不卡提供高清流畅的在线观影体验。',
        url: 'https://777cinema.com/about',
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: FAQ_DATA.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }} />

      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="px-4 md:px-6 py-6">
            <div className="max-w-[1000px] mx-auto">
              <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-100 mb-4">
                  关于 <span className="text-primary-500">777影院</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                  {ABOUT_INFO.tagline}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
                {ABOUT_INFO.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-800/50">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="prose prose-invert max-w-none mb-14">
                {ABOUT_INFO.description.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6 text-center">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
                {ABOUT_INFO.features.map((feature) => (
                  <div key={feature.title} className="p-5 rounded-lg bg-background-800/50 hover:bg-background-700/50 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-3">
                      <i className={`${feature.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-200 mb-2">{feature.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-14">
                {TESTIMONIALS.map((t, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-800/50">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <div className="text-sm font-medium text-foreground-200">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.date}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-0.5">
                        {Array.from({ length: t.rating }, (_, j) => (
                          <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed">{t.comment}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6 text-center">常见问题</h2>
              <div className="space-y-3 mb-14">
                {FAQ_DATA.map((faq, i) => (
                  <div key={i} className="rounded-lg bg-background-800/50 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-background-700/30 transition-colors"
                    >
                      <span className="text-sm font-medium text-foreground-200">{faq.question}</span>
                      <div className="w-6 h-6 flex items-center justify-center shrink-0">
                        <i className={`text-foreground-500 transition-transform duration-300 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6 text-center">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-14">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: CONTACT_INFO.email },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: CONTACT_INFO.wechat },
                  { icon: 'ri-phone-line', label: '客服电话', value: CONTACT_INFO.phone },
                  { icon: 'ri-time-line', label: '服务时间', value: CONTACT_INFO.workingHours },
                ].map((item) => (
                  <div key={item.label} className="p-5 rounded-lg bg-background-800/50 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                    <div className="text-sm text-foreground-300 font-medium">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}